import {
  Avatar,
  Box,
  Button,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { PriorityHigh as PriorityHighIcon } from '@mui/icons-material';
import { useContext } from 'react';
import { UserConversation } from '../../types/chats';
import { getShortRelativeTime } from '../../utils/global/helper';
import useUser from '../../hooks/global/useUser';
import { LoadedUser } from '../../interfaces';
import LimitedLinesTypography from '../global/LimitedLinesTypography';
import { AppContext } from '../../context/AppContext/context';
import {
  getChatParticipants,
  getParticipantDisplayName,
  isConversationImportant,
} from '../../helpers/chats';

interface Props {
  conversation: UserConversation;
  isSelected?: boolean;
  onSelect: (conversation: UserConversation) => void;
}

function InboxMessage({ conversation, isSelected, onSelect }: Props) {
  const {
    state: { unreadConversations },
  } = useContext(AppContext);
  const { user } = useUser<LoadedUser>();
  const theme = useTheme();

  const unreadMessageCount = unreadConversations[conversation.id] ?? 0;
  const { customerParticipant, otherParticipants, initials } =
    getChatParticipants(conversation, user);
  const isImportant = isConversationImportant({
    conversation,
    userId: user.userId,
  });

  const lastMessageTimestamp = conversation.lastMessageAt
    ? getShortRelativeTime(new Date(conversation.lastMessageAt))
    : '';

  return (
    <Button
      sx={{
        position: 'relative',
        width: '100%',
        p: 2.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'start',
        textAlign: 'left',
        gap: 1.25,
        boxSizing: 'border-box',
        borderRadius: '20px',
        bgcolor: isSelected ? theme.palette.secondary.main : 'transparent',
        color: isSelected ? '#ffffff' : 'text.primary',
        ':hover': {
          bgcolor: isSelected ? undefined : theme.palette.lightGrey.main,
        },
        mb: 0.5,
      }}
      type="button"
      onClick={() => onSelect(conversation)}>
      <Box
        position="absolute"
        left={6}
        flexShrink={0}
        boxSizing="border-box"
        height="9px"
        width="9px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgcolor={
          (unreadMessageCount || 0) <= 0
            ? 'transparent'
            : isSelected
            ? theme.palette.accent.main
            : theme.palette.secondary.main
        }
        borderRadius="50%"
      />
      <Avatar
        sx={{
          bgcolor: isImportant
            ? theme.palette.primary.light
            : theme.palette.accent.main,
        }}>
        {isImportant ? (
          <PriorityHighIcon
            sx={{
              color: theme.palette.primary.main,
              fontSize: '1.5rem',
            }}
          />
        ) : (
          <Typography color={theme.palette.secondary.main} variant="h5">
            {initials}
          </Typography>
        )}
      </Avatar>
      <Stack flex={1} gap={0.5}>
        <Box
          display="flex"
          alignItems="start"
          justifyContent="space-between"
          gap={2}>
          <Typography variant="h5" color="inherit">
            {getParticipantDisplayName(customerParticipant)}
            {otherParticipants.length > 0 && (
              <Typography variant="body2" component="span" color="inherit">
                {' '}
                (and {otherParticipants.length} other
                {otherParticipants.length > 1 ? 's' : ''})
              </Typography>
            )}
          </Typography>

          {lastMessageTimestamp && (
            <Typography variant="body2" color="inherit">
              {lastMessageTimestamp}
            </Typography>
          )}
        </Box>

        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={0.1}>
          <LimitedLinesTypography
            variant="body2"
            color="inherit"
            lines={2}
            sx={{
              opacity: conversation?.lastMessage?.text ? 1 : 0.6,
            }}>
            {conversation?.lastMessage?.text ||
              (conversation?.lastMessage?.attachment?.dimensions
                ? 'Attachment'
                : 'No messages yet')}
          </LimitedLinesTypography>
        </Box>
      </Stack>
    </Button>
  );
}

export default InboxMessage;
